import {
  computed,
  Directive,
  effect,
  EmbeddedViewRef,
  inject,
  input,
  signal,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';

import { AbilitiesSelectors } from '@recruitee/abilities-data';
import { Ability } from '@recruitee/abilities-types';
import { CurrentUserSelectors } from '@recruitee/account-data';
import { Candidate } from '@recruitee/candidate-types';
import { SubscriptionService } from '@recruitee/company-logic';
import { DevFlag, injectDevFlag } from '@recruitee/dev-flags-data';
import { EnabledIntegrationKindsSelectors } from '@recruitee/integrations-data';
import { IntegrationKinds } from '@recruitee/integrations-types';

@Directive({
  selector: '[rtKiwiHrPromoGuard]',
  standalone: true,
})
export class KiwiHrPromoGuardDirective {
  private readonly vcRef = inject(ViewContainerRef);

  private readonly templateRef = inject(TemplateRef);

  private readonly store = inject(Store);

  private readonly hasPromoteKiwiHR = injectDevFlag(DevFlag.PromoteKiwiHR);

  private readonly isTrial = inject(SubscriptionService).isTrial();

  private readonly wasClosed = this.store.selectSignal(
    CurrentUserSelectors.featureDiscovery({ feature: 'promoteKiwihrClosed' }),
  );

  private readonly canManageCandidates = this.store.selectSignal(
    AbilitiesSelectors.hasOne(Ability.ManageCandidates),
  );

  private readonly isKiwiHREnabled = this.store.selectSignal(
    EnabledIntegrationKindsSelectors.hasOne(IntegrationKinds.KIWI_HR),
  );

  public candidate = input<Candidate | undefined, Candidate | undefined | ''>(undefined, {
    alias: 'rtKiwiHrPromoGuard',
    transform: (candidate: Candidate | undefined | '') => candidate || undefined,
  });

  private isCandidateHired = computed(
    () => this.candidate()?.placements.some(placement => placement.hiredInThisPlacement) ?? false,
  );

  private showPromo = computed(
    () =>
      this.hasPromoteKiwiHR &&
      !this.isTrial &&
      !this.wasClosed() &&
      this.canManageCandidates() &&
      !this.isKiwiHREnabled() &&
      (!this.candidate() || this.isCandidateHired()),
  );

  private embeddedViewRef = signal<EmbeddedViewRef<any> | null>(null);

  private watchEmbeddedCmpInputChanges = effect(() => this.embeddedViewRef()?.detectChanges());

  private showOrHidePromo$ = toObservable(this.showPromo)
    .pipe(takeUntilDestroyed())
    .subscribe(showPromo => {
      this.vcRef.clear();
      const view = showPromo ? this.vcRef.createEmbeddedView(this.templateRef) : null;
      this.embeddedViewRef.set(view);
    });
}
